import React from 'react';

export const transformOptionsArticle = {
  id: 'geometry-transform-passes',
  title: 'Geometry Transform Passes',
  description: 'Deep dive into geometry transforms that prepare your model for compression.',
  category: 'advanced',
  lastUpdated: '2025-06-29',
  tags: ['transform', 'geometry', 'optimization'],
  content: () => (
    <div>
  <div className="space-y-6 text-muted-foreground leading-relaxed">
        <p>
          Transform passes are the first step in the pipeline. Think of them as <em>house-keeping</em> for your scene –
          they strip dead weight and reorganise data so later compression stages work smarter, not harder.
        </p>

  <h2 className="text-xl font-semibold text-foreground mb-3">Why transforms matter</h2>
        <p>
          A glTF can contain redundant vertices, unused attributes, deeply nested node hierarchies and dozens of
          draw-calls that stall mobile GPUs.  Running transforms before compression pays dividends: you often save
          10-40 % in file size <strong>before Draco kicks in</strong>.
        </p>

  <h2 className="text-xl font-semibold text-foreground mb-3">Options</h2>
        <ul className="list-disc list-inside space-y-2 ml-4">
          <li><strong>Remove duplicate vertices</strong> – Uses hashing to find vertices with identical <code>POSITION</code> / <code>NORMAL</code> / <code>UV</code> data. Expect ~5% savings on CAD exports. See the official documentation on <a href="https://gltf-transform.dev/modules/functions/functions/dedup" target="_blank" rel="noopener noreferrer" className="text-accent hover:text-accent/90">deduplication</a>.</li>

          <li><strong>Remove unused vertices</strong> – Purges stray data after mesh edits. Especially useful when baking lightmaps which sometimes leave orphan verts.</li>

          <li><strong>Geometry simplification</strong> – Implements a Quadric Error Metrics (QEM) decimator, first presented by Garland & Heckbert in 1997. Use values between 0.2-0.5 for hero assets and 0.05 for background props. The original paper is an excellent resource: <a href="http://www.cs.cmu.edu/~garland/Papers/quadrics.pdf" target="_blank" rel="noopener noreferrer" className="text-accent hover:text-accent/90">Surface Simplification Using Quadric Error Metrics</a>.</li>

          <li><strong>Instance meshes</strong> – Converts identical meshes into GPU instancing. WebGL2 can push thousands of instances in a single draw-call. The <a href="https://webgl2fundamentals.org/webgl/lessons/webgl-instanced-drawing.html" target="_blank" rel="noopener noreferrer" className="text-accent hover:text-accent/90">WebGL2 Fundamentals guide</a> provides a great practical example.</li>

          <li><strong>Flatten nodes</strong> – Removes empty parents; good practice for AR where deep hierarchies hurt performance.</li>

          <li><strong>Merge meshes</strong> – Detects meshes that share the <em>same</em> material and bakes them into a single draw-call.  This is the easiest way to cut WebGL CPU overhead on scenes with dozens of small parts (e.g. screws, bolts).  UVs and tangents are preserved – but if two meshes reference <em>different</em> materials they stay separate to prevent visual glitches.</li>

          <li><strong>Weld vertices</strong> – Merges vertices within a tolerance (0.0001 m).  Useful after boolean ops in CAD that leave micro gaps.</li>
        </ul>

  <h2 className="text-xl font-semibold text-foreground mb-3">Best Practices</h2>
        <ul className="list-disc list-inside space-y-2 ml-4">
          <li>Run <strong>remove duplicate + weld</strong> first – they often enable bigger wins later.</li>
          <li>Simplify <em>before</em> texturing; materials baked to high-poly meshes can break UVs.</li>
          <li>For animated characters, avoid aggressive welding which may break skin weight continuity.</li>
        </ul>
      </div>
    </div>
  )
}; 